// src/pages/Support.js
import React, { useState } from 'react';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Thank you for your message! We\'ll get back to you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      category: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="page-container">
      <div className="last-updated">
        <strong>Support Center</strong> - Last updated: June 2026
      </div>

      <h1>Support Center</h1>
      <p>
        Welcome to the AI Vision App Support Center. We're here to help you get the most 
        out of your AI-powered object recognition experience.
      </p>

      <div className="grid grid-2">
        <div>
          <div className="card">
            <h2>Frequently Asked Questions</h2>
            
            <h3>Getting Started</h3>
            <div style={{ marginLeft: '1rem' }}>
              <h4>How do I take my first detection?</h4>
              <p>
                Open the app, point your camera at any object, and AI Vision will automatically
                detect and identify it in real-time with floating AR labels. Tap a label to see
                details and take actions like search, share, or speak.
              </p>

              <h4>What can AI Vision App detect?</h4>
              <p>
                AI Vision supports 6 detection modes: Objects, Text (OCR), Barcodes/QR codes,
                Plants, Food, and Landmarks. Each mode is optimized for its category using
                Google's Gemini AI.
              </p>

              <h4>Do I need an internet connection?</h4>
              <p>
                Yes, an internet connection is required for AI-powered detection. Barcode/QR code
                scanning works offline, but product lookup requires internet.
              </p>
            </div>

            <h3>Premium Features</h3>
            <div style={{ marginLeft: '1rem' }}>
              <h4>What's included in Premium?</h4>
              <p>Premium includes:</p>
              <ul className="legal-list">
                <li>Unlimited scans (no 5/day limit)</li>
                <li>Up to 5 objects per scan (vs 3 for free)</li>
                <li>Faster 1-second detection intervals</li>
                <li>Fun facts and estimated pricing for objects</li>
                <li>Cloud image backup</li>
                <li>Batch capture mode and data export</li>
                <li>Ad-free experience</li>
              </ul>

              <h4>How do I upgrade to Premium?</h4>
              <p>
                Go to Settings {'>'} Premium or tap the diamond icon in the app.
                Choose from monthly ($3.99) or yearly ($29.99, save 37%).
              </p>

              <h4>Can I cancel my subscription?</h4>
              <p>
                Yes, you can cancel anytime through your App Store or Google Play account. 
                You'll retain premium features until the end of your billing period.
              </p>
            </div>

            <h3>Technical Issues</h3>
            <div style={{ marginLeft: '1rem' }}>
              <h4>The camera isn't working</h4>
              <p>
                Make sure you've granted camera permissions to AI Vision App in your device 
                settings. Try restarting the app or your device if the issue persists.
              </p>

              <h4>Detection is slow or inaccurate</h4>
              <p>
                Ensure good lighting and hold your device steady. The app will show a
                "Scanning..." indicator while processing. You'll feel a gentle vibration
                when an object is detected. Clean your camera lens and keep objects in clear view.
              </p>

              <h4>App crashes or freezes</h4>
              <p>
                Try closing and reopening the app. If problems continue, restart your device 
                or reinstall the app. Contact support if issues persist.
              </p>
            </div>

            <h3>Account & Data</h3>
            <div style={{ marginLeft: '1rem' }}>
              <h4>How do I backup my data?</h4>
              <p>
                Premium users can enable cloud sync in Settings. Free users can export 
                their detection history manually through the app.
              </p>

              <h4>How do I delete my account?</h4>
              <p>
                Go to Settings {'>'} Account {'>'} Delete Account. This will permanently remove 
                all your data and cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="contact-form">
            <h2>Contact Support</h2>
            <p>
              Can't find what you're looking for? Send us a message and we'll help you out.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">Select a category</option>
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing & Subscriptions</option>
                  <option value="feature">Feature Request</option>
                  <option value="bug">Bug Report</option>
                  <option value="general">General Question</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please describe your issue or question in detail..."
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn" style={{ width: '100%' }}>
                Send Message
              </button>
            </form>

            <div className="highlight-box" style={{ marginTop: '2rem' }}>
              <h3>Response Time</h3>
              <p>
                We typically respond to support requests within 24 hours. 
                Premium users receive priority support with faster response times.
              </p>
            </div>
          </div>

          <div className="card" style={{ marginTop: '2rem' }}>
            <h3>Quick Tips</h3>
            <ul className="legal-list">
              <li><strong>Good Lighting:</strong> Ensure adequate lighting for better detection accuracy</li>
              <li><strong>Stable Hands:</strong> Hold your device steady for clearer captures</li>
              <li><strong>Close Range:</strong> Objects should be clearly visible and not too far away</li>
              <li><strong>Clean Lens:</strong> Keep your camera lens clean for optimal results</li>
              <li><strong>Updates:</strong> Keep the app updated for the latest AI improvements</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '3rem' }}>
        <h2>System Requirements</h2>
        <div className="grid grid-2">
          <div>
            <h3>iOS Requirements</h3>
            <ul className="legal-list">
              <li>iOS 13.0 or later</li>
              <li>iPhone 7 or newer</li>
              <li>2GB RAM minimum</li>
              <li>100MB available storage</li>
              <li>Camera access required</li>
            </ul>
          </div>
          <div>
            <h3>Android Requirements</h3>
            <ul className="legal-list">
              <li>Android 8.0 (API level 26) or later</li>
              <li>2GB RAM minimum</li>
              <li>100MB available storage</li>
              <li>Camera2 API support</li>
              <li>OpenGL ES 3.0</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;